import { createFileRoute } from "@tanstack/react-router";
import { generateBuilding } from "building-generation";
import type { GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import { DEFAULT_OPTION_CHOICES, DEFAULT_STUDIO_DOC_NAME } from "building-types/doc/docDefaultConstants";
import type { FaceCategory } from "building-types/materials/faceCategories";
import type { FaceHashRenderTypeKey } from "building-types/materials/faceHashTypes";
import {
  FaceDiffType,
  type FaceDiffTypeSettings,
  type FaceHashRenderSettings,
} from "building-types/materials/faceHashTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import { PAGE_WRAPPER_ID, WEB_API_BASE_PATH } from "common/constants";
import { remoteDbs } from "common/constants/db";
import Flags from "common/constants/flags";
import { ensureEnumValue } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { camelCaseToTitleCase, snakeCaseToTitleCase } from "common/utils/strings";
import { unmarshalTypedArraysInObject } from "common/utils/unmarshalTypedArraysInObject";
import { defaultDoc } from "doc/docDefault";
import { docParseJSON } from "doc/docMarshal";
import { getDocWithOverrides } from "doc/options/mergeOptionTemplatesIntoOptionSets";
import { DIST_EPSILON } from "geom/core";
import { EnvPreset } from "gfx/EnvPreset";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import DebugBuildingViewer from "lib/components/design/DebugBuildingViewer";
import InspectorTable from "lib/components/studio/inspector/InspectorTable";
import { useFetchImmutable } from "lib/fetcher";
import type { FaceHashMapInfo } from "lib/house/buildingEntityFaceHashTools";
import {
  buildingEntityFaceHashMapsToLogInfo,
  makeBuildingEntityFaceHashDiff,
  operateBuildingEntityFaceHashMaps,
} from "lib/house/buildingEntityFaceHashTools";
import { faceDiffEntityMaskFromHashMaps, type BuildingEntityFaceHashMaps } from "lib/house/buildingEntityFaceHashTypes";
import {
  BuildingViewerViewMode,
  SiteType,
  defaultRenderFaceDiffSettings,
  defaultSceneState,
} from "lib/house/render/buildingViewerSceneState";
import { defaultFaceDiffView3DFilter } from "lib/house/render/viewFilterDefaults";
import createCustomizerStore from "lib/state/createCustomizerStore";
import { setLoading, updateBuildingDoc } from "lib/state/customizerActions";
import type { Customizer } from "lib/state/customizerContext";
import { LoadState, useCustomizerDispatch, useCustomizerSelector } from "lib/state/customizerContext";
import { setDebugFaceDiff, setFaceDiffSettings, setViewFilter, setViewMode } from "lib/state/sceneStateActions";
import { getWithTimeout } from "lib/utils/fetchAuthHelpers";
import getPageTitle from "lib/utils/getPageTitle";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Helmet as Head } from "react-helmet-async";
import { Provider } from "react-redux";
import { makeFaceDiffTestHashMaps } from "test/util/faceDiffTestHashMaps";
import Loading from "ui/components/Loading";
import cx from "ui/cx";
import Checkbox from "ui/forms/CheckboxOld";
import ToggleGroup from "ui/forms/ToggleGroup";
import useQueryParams from "ui/hooks/useQueryParams";
import colors from "ui/styles/colors";
import withAuthentication from "../../components/withAuthentication";
import styles from "./face-diff-viewer.module.scss";

const integrationBaseUrl = `/testResultsIntegration/tmpOutput/faceDiff`;
const promotionBaseUrl = `/testResultsPromotion/tmpOutput/faceDiff`;

const defaultFaceDiffTypeSettings: FaceDiffTypeSettings = {
  [FaceDiffType.STATIC]: {
    renderLines: true,
  },
  [FaceDiffType.ADDED]: {
    renderLines: true,
    renderMeshes: true,
  },
  [FaceDiffType.REMOVED]: {
    renderLines: true,
  },
};

const initialState: Customizer = {
  building: null,
  fatalError: null,
  sceneState: {
    ...defaultSceneState,
    faceDiffEnabled: true,
    devCameraControls: true,
    siteType: SiteType.GROUND_PLANE_DISABLE_SITE,
    envPreset: EnvPreset.WHITEBOX,
    exteriorLightProbesEnabled: false,
    faceDiffSettings: {
      ...defaultRenderFaceDiffSettings,
      faceDiffTypeSettings: defaultFaceDiffTypeSettings,
    },
  },
  loading: LoadState.FULLSCREEN,
  name: DEFAULT_STUDIO_DOC_NAME,
  optionChoices: DEFAULT_OPTION_CHOICES,
};

const store = createCustomizerStore(initialState);

enum FaceDiffViewMode {
  BASELINE = "BASELINE",
  TEST = "TEST",
  BOTH = "BOTH",
}

enum FaceTypeSetFilter {
  ALL_FACE_TYPES = "ALL_FACES",
  CHANGED_FACE_TYPES = "CHANGED_FACE_TYPES",
}

function FaceDiffViewerInternal() {
  const { viewFilter, loading, building, faceDiffSettings } = useCustomizerSelector((cus) => ({
    building: cus.building,
    loading: cus.loading,
    viewFilter: cus.sceneState.viewFilter,
    faceDiffSettings: cus.sceneState.faceDiffSettings,
  }));
  const dispatch = useCustomizerDispatch();

  // If circleJobId is set, test results are loaded from the corresponding CircleCI job,
  // and test homes are loaded from the current branch (or from branchOverride, if that parameter is set).
  const { testType, circleJobId, branchOverride, featureFlags } = useQueryParams([
    "testType",
    "circleJobId",
    "branchOverride",
    "featureFlags",
  ]);
  const enabledFlags = new Set(featureFlags?.split(",").filter(Boolean) ?? []);
  const [selectedFailureKey, setSelectedFailureKey] = useState<any>();
  const baseUrl = testType === "promotion" ? promotionBaseUrl : integrationBaseUrl;

  // Mirrors the server's DB_ENV-based usingLocalDb() check, not Flags.IS_DEV_MODE — a dev-mode
  // client build can still point at a remote DB_ENV where this static route isn't registered.
  const canFetchLocalIndex = !remoteDbs.includes(Flags.DB_ENV);
  const localIndexUrl = canFetchLocalIndex ? `${baseUrl}/index.json` : "";
  const indexUrl = circleJobId
    ? `${WEB_API_BASE_PATH}/debug/face-diff-manifest?circleJobId=${circleJobId}`
    : localIndexUrl;
  const { data: testFailureIndex }: any = useFetchImmutable(indexUrl);
  const noLocalResultsAvailable = !circleJobId && !canFetchLocalIndex;

  const [selectedDocName, setSelectedDocName] = useState<string>();
  const [selectedSetName, setSelectedSetName] = useState<string>();
  const [selectedSetType, setSelectedSetType] = useState<string>();
  const [selectedBaselineHash, setSelectedBaselineHash] = useState<BuildingEntityFaceHashMaps>();

  const [selectedChangeSet, setSelectedChangeSet] = useState<BuildingEntityFaceHashMaps>();

  const [faceDiffViewMode, setFaceDiffViewMode] = useState(FaceDiffViewMode.BOTH);
  const [faceDiffEntityFilter, setFaceDiffEntityFilter] = useState(FaceTypeSetFilter.CHANGED_FACE_TYPES);
  const [highlightChanges, setHighlightChanges] = useState(true);
  const [showStatic, setShowStatic] = useState(false);
  const [isFaceDiffLoading, setIsFaceDiffLoading] = useState(false);

  // When changes are highlighted, custom visibility should be disabled and the view mode should not be BOTH.
  useEffect(() => {
    if (!highlightChanges) {
      setFaceDiffViewMode(FaceDiffViewMode.BASELINE);
      setCustomVisibility(false);
    }
  }, [highlightChanges]);

  const [customVisibility, setCustomVisibility] = useState(false);
  const [baseSettings, setBaseSettings] = useState<FaceDiffTypeSettings>(defaultFaceDiffTypeSettings);

  const viewerSystem = useRef<THREEViewerSystem>(null);

  /**
   * Viewer Setup. We only need to run this once, since we don't actually re-render or generate the
   * building using the standard studio process
   */
  useMemo(() => {
    dispatch(setLoading(LoadState.FULLSCREEN));
    dispatch(updateBuildingDoc({}, defaultDoc("face-diff-viewer")));
    dispatch(setViewMode(BuildingViewerViewMode.EXTERIOR));
    dispatch(setViewFilter({ ...defaultFaceDiffView3DFilter, ...viewFilter }));
  }, []);

  /**
   * Load initial testFailure on index change
   */
  useEffect(() => {
    const keys = Object.keys(testFailureIndex ?? {});
    if (keys.length > 0) {
      setSelectedFailureKey(keys[0]);
    }
  }, [testFailureIndex]);

  /**
   * Main trigger for selecting the baseline failure and generating the faceHashes.
   * This is run whenever the user selection for the doc+option failure changes
   */
  useEffect(() => {
    if (!selectedFailureKey || !testFailureIndex[selectedFailureKey]) {
      return;
    }

    setIsFaceDiffLoading(true);

    const selectedFailure = testFailureIndex[selectedFailureKey];
    let docFetchPromise, diffFetchPromise;

    if (circleJobId) {
      const branch = encodeURIComponent(branchOverride ? branchOverride : Flags.GIT_BRANCH);

      docFetchPromise = getWithTimeout(
        `${WEB_API_BASE_PATH}/debug/github-file-url?file=${selectedFailure.input}&branch=${branch}&testType=${
          testType ? testType : "integration"
        }`,
      )
        .then((response) => response.json())
        .then((responseJson) => fetch(responseJson.url))
        .then((response) => response.json());

      diffFetchPromise = getWithTimeout(
        `${WEB_API_BASE_PATH}/debug/circleci-artifact?url=${selectedFailure.diff}`,
      ).then((response) => response.json());
    } else {
      docFetchPromise = fetch(`${baseUrl}/${selectedFailure.input}`).then((response) => response.json());
      diffFetchPromise = fetch(`${baseUrl}/${selectedFailure.diff}`).then((response) => response.json());
    }

    Promise.all([docFetchPromise, diffFetchPromise])
      .then(async (values) => {
        const errors: string[] = [];
        const warnings: string[] = [];
        const doc = getDocWithOverrides(docParseJSON(values[0], undefined, warnings, errors));
        const optionChoices = selectedFailure.optionChoices ?? doc.optionChoices ?? DEFAULT_OPTION_CHOICES;

        const faceChangeSet = unmarshalTypedArraysInObject(values[1]);
        const featureSet: Partial<GenerateBuildingFeatureSet> = {
          wallPlanes: enabledFlags.has("wallPlanes"),
          horizontalAssemblies: enabledFlags.has("horizontalAssemblies"),
          roofFraming: enabledFlags.has("roofFraming"),
          modelComponents: enabledFlags.has("modelComponents"),
        };
        let faceBaseline = await makeBaselineBuildingEntityFaceHashMaps(
          doc,
          optionChoices,
          selectedFailure.useExportPipelineGeometry,
          selectedFailure.buildMaterialGeometry,
          featureSet,
        );
        if (!circleJobId) {
          /**
           * circleJobId is typically undefined if the viewer is being run on a local
           * branch. In this case, the faceBaseline has the changeSet already applied, and it needs to be
           * removed.
           *
           * @TODO - this should be removed once we can load baselines statically from a branch,
           * rather than computing them using generateBuilding.
           */
          faceBaseline = operateBuildingEntityFaceHashMaps(faceBaseline, faceChangeSet, "SUBTRACT", {
            faceReturnTypeFilter: {
              [FaceDiffType.ADDED]: true,
            },
          });
        }

        setSelectedBaselineHash(faceBaseline);
        setSelectedChangeSet(faceChangeSet);
        setSelectedDocName(doc.name);
        const splitSelectedFailureKey = selectedFailureKey.split("_");
        const presetOrOptionUID = splitSelectedFailureKey[splitSelectedFailureKey.length - 1];
        let name = doc.presets?.[presetOrOptionUID]?.name;

        if (name) {
          setSelectedSetType("PRESET");
        }

        if (!name) {
          Object.keys(doc.optionSets).forEach((optionSetId) => {
            const options = doc.optionSets[optionSetId].options;
            const option = options[presetOrOptionUID];
            if (option) {
              name = option.name;
              return;
            }
          });
          setSelectedSetType("OPTION");
        }
        if (!name) {
          setSelectedSetType("UNKNOWN");
        }
        setSelectedSetName(name ?? presetOrOptionUID);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsFaceDiffLoading(false);
      });
  }, [selectedFailureKey]);

  useEffect(() => {
    const newFaceDiffTypeSettings = { ...baseSettings };
    // When custom visibility is turned off, use the static column for all visibility settings.
    if (!customVisibility) {
      newFaceDiffTypeSettings[FaceDiffType.ADDED] = baseSettings[FaceDiffType.STATIC];
      newFaceDiffTypeSettings[FaceDiffType.REMOVED] = baseSettings[FaceDiffType.STATIC];
    }
    if (!showStatic) {
      newFaceDiffTypeSettings[FaceDiffType.STATIC] = {};
    }
    dispatch(
      setFaceDiffSettings({
        ...faceDiffSettings,
        faceDiffTypeSettings: newFaceDiffTypeSettings,
      }),
    );
  }, [baseSettings, showStatic, customVisibility, faceDiffViewMode, dispatch]);

  /**
   * Switch between BASELINE/TEST/DIFF view modes and ADDED_CHANGES/ALL_CHANGES diff filter
   */
  useEffect(() => {
    if (!selectedBaselineHash || !selectedChangeSet) {
      return;
    }

    let faceDiff: BuildingEntityFaceHashMaps;
    if (highlightChanges) {
      const appliedBaselineHash = operateBuildingEntityFaceHashMaps(selectedBaselineHash, selectedChangeSet, "ADD");
      faceDiff = operateBuildingEntityFaceHashMaps(appliedBaselineHash, selectedBaselineHash, "SUBTRACT", {
        faceReturnTypeFilter: {
          [FaceDiffType.STATIC]: showStatic,
          [FaceDiffType.ADDED]:
            faceDiffViewMode === FaceDiffViewMode.TEST || faceDiffViewMode === FaceDiffViewMode.BOTH,
          [FaceDiffType.REMOVED]:
            faceDiffViewMode === FaceDiffViewMode.BASELINE || faceDiffViewMode === FaceDiffViewMode.BOTH,
        },
      });
    } else {
      switch (faceDiffViewMode) {
        case FaceDiffViewMode.BASELINE: {
          faceDiff = makeBuildingEntityFaceHashDiff(selectedBaselineHash, selectedBaselineHash);
          break;
        }
        case FaceDiffViewMode.TEST: {
          const appliedBaselineHash = operateBuildingEntityFaceHashMaps(
            selectedBaselineHash,
            selectedChangeSet,
            "ADD",
            {
              faceReturnTypeFilter: {
                [FaceDiffType.ADDED]: true,
              },
            },
          );
          faceDiff = makeBuildingEntityFaceHashDiff(appliedBaselineHash, appliedBaselineHash);
          break;
        }
        case FaceDiffViewMode.BOTH: {
          const appliedBaselineHash = operateBuildingEntityFaceHashMaps(selectedBaselineHash, selectedChangeSet, "ADD");
          faceDiff = operateBuildingEntityFaceHashMaps(appliedBaselineHash, selectedBaselineHash, "SUBTRACT", {
            faceReturnTypeFilter: {
              [FaceDiffType.STATIC]: showStatic,
              [FaceDiffType.ADDED]: true,
              [FaceDiffType.REMOVED]: true,
            },
          });
          break;
        }
        default: {
          assertUnreachable(faceDiffViewMode);
        }
      }
    }
    dispatch(setDebugFaceDiff(faceDiff));
  }, [selectedBaselineHash, selectedChangeSet, faceDiffViewMode, highlightChanges, showStatic]);

  /**
   * Switch beween CHANGED_FACE_TYPES/ALL_FACE_TYPES visibility controls
   */
  useEffect(() => {
    if (!selectedChangeSet || !selectedBaselineHash) {
      return;
    }
    let faceDiffEntityMask: Set<FaceCategory>;
    switch (faceDiffEntityFilter) {
      case FaceTypeSetFilter.CHANGED_FACE_TYPES: {
        faceDiffEntityMask = faceDiffEntityMaskFromHashMaps(selectedChangeSet);
        break;
      }
      case FaceTypeSetFilter.ALL_FACE_TYPES: {
        // Combine the entity masks of the baseline and change set to show face types
        // that are only present in one of the sets.
        const baselineEntityMask = faceDiffEntityMaskFromHashMaps(selectedBaselineHash);
        const changeSetEntityMask = faceDiffEntityMaskFromHashMaps(selectedChangeSet);

        faceDiffEntityMask = new Set([...baselineEntityMask, ...changeSetEntityMask]);

        break;
      }
      default: {
        assertUnreachable(faceDiffEntityFilter);
      }
    }
    dispatch(
      setFaceDiffSettings({
        ...faceDiffSettings,
        faceDiffEntityMask,
      }),
    );
  }, [faceDiffEntityFilter, selectedChangeSet, selectedBaselineHash]);

  /**
   * Next/Prev button handlers
   */
  const setNext = () => {
    const keys = Object.keys(testFailureIndex ?? {});
    const currentIndex = keys.indexOf(selectedFailureKey);
    const nextIndex = (currentIndex + 1) % keys.length;
    setSelectedFailureKey(keys[nextIndex]);
  };

  const setPrev = () => {
    const keys = Object.keys(testFailureIndex ?? {});
    const currentIndex = keys.indexOf(selectedFailureKey);
    const prevIndex = (currentIndex - 1 + keys.length) % keys.length;
    setSelectedFailureKey(keys[prevIndex]);
  };

  /*
  Add actions happening when left, right arrow keys are pressed
  */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        setNext();
      } else if (e.key === "ArrowLeft") {
        setPrev();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedFailureKey]);

  /**
   * Render code below this line
   */

  const changeSetTypeLogs: FaceHashMapInfo[] = [];
  const changeSetMaterialLogs: FaceHashMapInfo[] = [];

  if (selectedChangeSet) {
    changeSetTypeLogs.push(...buildingEntityFaceHashMapsToLogInfo(selectedChangeSet, false));
    changeSetMaterialLogs.push(...buildingEntityFaceHashMapsToLogInfo(selectedChangeSet, true));
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Face Diff Viewer")}</title>
      </Head>

      {isFaceDiffLoading && <div className={styles.faceDiffLoadingBanner}>Loading...</div>}

      {noLocalResultsAvailable && (
        <div className={styles.faceDiffInfoBanner}>
          No local test results available in this environment. Provide <code>?circleJobId=&lt;id&gt;</code> in the URL
          to view CI test failures, or run this page on a local dev server after running the tests.
        </div>
      )}

      <div className={styles.paramsContainer} style={{ marginTop: "75px" }}>
        <label className={styles.categoryModeLabel}>Change Sets:</label>
        <select // html select maintains focus after selection, so we can iterate test failures using arrows
          value={selectedFailureKey ?? ""}
          onChange={(e) => {
            setSelectedFailureKey(e.target.value);
          }}
          className={styles.faceDiffSelect}
        >
          {Object.keys(testFailureIndex ?? {}).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
        <div>
          <button onClick={setPrev}>Prev</button>
          <button onClick={setNext}>Next</button>
        </div>

        <div style={{ width: "100%" }}>
          <InspectorTable
            dataSrc={{
              faceDiffViewMode,
              faceDiffEntityFilter,
              selectedDocName,
              selectedSetName,
              selectedSetType,
            }}
            onChange={(_key: string, _v: any) => {
              return;
            }}
            rows={[
              {
                type: "DISPLAY",
                title: "Doc Name",
                key: "selectedDocName",
              },
              { type: "DISPLAY", title: "Set Name", key: "selectedSetName" },
              { type: "DISPLAY", title: "Set Type", key: "selectedSetType" },
            ]}
          />
        </div>
        <div style={{ width: "100%", marginTop: "12px", marginBottom: "10px" }} className={styles.viewModeToggleGroup}>
          <ToggleGroup.Root
            type="single"
            value={faceDiffViewMode}
            onValueChange={(v: string) => v && setFaceDiffViewMode(ensureEnumValue(v, FaceDiffViewMode))}
            allowEmpty={false}
            toggleStyle="tertiary"
            className={styles.fullWidth}
          >
            <ToggleGroup.Item value={FaceDiffViewMode.BASELINE}>Baseline</ToggleGroup.Item>
            <ToggleGroup.Item value={FaceDiffViewMode.TEST}>Test</ToggleGroup.Item>
            <ToggleGroup.Item value={FaceDiffViewMode.BOTH} disabled={!highlightChanges}>
              Both
            </ToggleGroup.Item>
          </ToggleGroup.Root>
        </div>
        <div style={{ width: "100%" }} className={styles.inspectorTableWideLabels}>
          <InspectorTable
            dataSrc={{
              faceDiffViewMode,
              faceDiffEntityFilter,
              selectedDocName,
              selectedSetName,
              selectedSetType,
              highlightChanges,
              showStatic,
            }}
            onChange={(_key: string, _v: any) => {
              return;
            }}
            rows={[
              {
                type: "ENUM",
                values: FaceTypeSetFilter,
                title: "Face Mode",
                key: "faceDiffEntityFilter",
                onChange: (v: string) => setFaceDiffEntityFilter(ensureEnumValue(v, FaceTypeSetFilter)),
              },
              {
                type: "CHECKBOX",
                title: "Highlight Changes",
                key: "highlightChanges",
                onChange: (v: boolean) => setHighlightChanges(v),
              },
              {
                type: "CHECKBOX",
                title: "Show Static",
                key: "showStatic",
                onChange: (v: boolean) => setShowStatic(v),
              },
            ]}
          />
        </div>
        <label className={styles.categoryModeLabel} style={{ display: "block", marginTop: "8px", marginBottom: "4px" }}>
          Visibility
        </label>
        <FaceDiffVisibilityGrid
          showStatic={showStatic}
          faceDiffViewMode={faceDiffViewMode}
          baseSettings={baseSettings}
          setBaseSettings={setBaseSettings}
          customVisibility={customVisibility}
        />
        <div className={styles.visibilityGridRow}>
          <span className={styles.visibilityGridRowLabel}>Custom Visibility</span>
          <div className={styles.visibilityGridCell}>
            <Checkbox checked={customVisibility} onChange={(e) => setCustomVisibility(!!e.currentTarget.checked)} />
          </div>
        </div>
      </div>

      <div className={styles.testFailuresContainer}>
        <label className={styles.categoryModeLabel}>Global Change Info:</label>
        <FaceDiffFalures info={changeSetTypeLogs}></FaceDiffFalures>
        <br></br>
        <label className={styles.categoryModeLabel}>Material Specific Change Info:</label>
        <FaceDiffFalures info={changeSetMaterialLogs}></FaceDiffFalures>
      </div>

      {building && (
        <div className={styles.viewerContainer} id={PAGE_WRAPPER_ID}>
          {loading && <Loading />}
          <DebugBuildingViewer viewerSystem={viewerSystem} />
        </div>
      )}
    </>
  );
}

const VISIBILITY_ROW_KEYS: {
  key: keyof FaceHashRenderSettings;
  label: string;
  indent?: boolean;
}[] = [
  { key: "renderLines", label: "Lines" },
  { key: "renderMeshes", label: "Mesh" },
  { key: "renderMeshMaterials", label: "Material", indent: true },
  { key: "renderMeshOpacity", label: "Opaque", indent: true },
];

const VISIBILITY_COLUMNS = [FaceDiffType.STATIC, FaceDiffType.ADDED, FaceDiffType.REMOVED] as const;

function FaceDiffVisibilityGrid({
  baseSettings,
  setBaseSettings,
  customVisibility,
  faceDiffViewMode,
  showStatic,
}: {
  baseSettings: FaceDiffTypeSettings;
  setBaseSettings: (settings: FaceDiffTypeSettings) => void;
  customVisibility: boolean;
  faceDiffViewMode: FaceDiffViewMode;
  showStatic: boolean;
}) {
  const columns = customVisibility ? VISIBILITY_COLUMNS : [FaceDiffType.STATIC];
  const showHeaderRow = customVisibility;

  return (
    <div className={cx(styles.visibilityGrid, !customVisibility && styles.visibilityGridSingleColumn)}>
      {showHeaderRow && <div className={styles.visibilityGridHeader} />}
      {showHeaderRow &&
        VISIBILITY_COLUMNS.map((faceType) => (
          <div key={faceType} className={styles.visibilityGridHeader}>
            {snakeCaseToTitleCase(faceType)}
          </div>
        ))}
      {VISIBILITY_ROW_KEYS.map(({ key, label, indent }) => (
        <Fragment key={key}>
          <div className={cx(styles.visibilityGridRowLabel, indent && styles.visibilityGridRowLabelIndent)}>
            {label}
          </div>
          {columns.map((faceType) => {
            const settings = baseSettings[faceType] ?? {};
            const checked = !!settings[key];
            const isMeshEnabled = baseSettings[faceType]?.renderMeshes ?? false;
            return (
              <div key={faceType} className={styles.visibilityGridCell}>
                <Checkbox
                  checked={checked}
                  disabled={isCheckboxDisabled(
                    faceType,
                    key,
                    showStatic,
                    faceDiffViewMode,
                    isMeshEnabled,
                    customVisibility,
                  )}
                  onChange={(e) => {
                    const next = !!e.currentTarget.checked;
                    const nextSettings = { ...settings, [key]: next };
                    setBaseSettings({ ...baseSettings, [faceType]: nextSettings });
                  }}
                />
              </div>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}

// Returns true if the checkbox should be disabled.
function isCheckboxDisabled(
  faceType: FaceDiffType,
  renderType: FaceHashRenderTypeKey,
  showStatic: boolean,
  faceDiffViewMode: FaceDiffViewMode,
  isMeshEnabled: boolean,
  customVisibility: boolean,
) {
  if (faceType === FaceDiffType.STATIC && !showStatic && customVisibility) {
    return true;
  }
  if (
    (faceType === FaceDiffType.ADDED && faceDiffViewMode === FaceDiffViewMode.BASELINE) ||
    (faceType === FaceDiffType.REMOVED && faceDiffViewMode === FaceDiffViewMode.TEST)
  ) {
    return true;
  }
  if (renderType === "renderMeshOpacity" || renderType === "renderMeshMaterials") {
    return !isMeshEnabled;
  }
  return false;
}

function FaceDiffFalures({ info }: { info: ReturnType<typeof buildingEntityFaceHashMapsToLogInfo> }) {
  const valueStyle = (k: keyof FaceHashMapInfo, v: any): CSSProperties => {
    switch (k) {
      case "faceAreaDelta":
      case "faceAABBShift":
        return Math.abs(v) < DIST_EPSILON ? { color: "green" } : { color: "red" };
      case "faceType":
        return { fontSize: 14 };
      case "faceMaterial":
        return { color: colors.gray60, fontSize: 12 };
      default:
        return {};
    }
  };

  const valueFormat = (k: keyof FaceHashMapInfo, v: any) => {
    if (k === "faceMaterial") {
      return (v as string).split(".").join("\n");
    }
    return v;
  };

  return (
    <>
      {info.map((failure, i) => (
        <div key={i} className={styles.failureLogSection}>
          {Object.entries(failure).map(([log, logValue], i) => (
            <div key={i} className={styles.failureLogRow}>
              <label>{camelCaseToTitleCase(log)}</label>
              <span style={valueStyle(log as keyof FaceHashMapInfo, logValue)}>
                {valueFormat(log as keyof FaceHashMapInfo, logValue)}
              </span>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function FaceDiffViewer() {
  return (
    <Provider store={store}>
      <FaceDiffViewerInternal />
    </Provider>
  );
}

async function makeBaselineBuildingEntityFaceHashMaps(
  doc: Doc,
  optionChoices: OptionChoices,
  useExportPipelineGeometry = false,
  buildMaterialGeometry = false,
  enabledFeatures: Partial<GenerateBuildingFeatureSet> = {},
) {
  const building = await generateBuilding({
    name: "test",
    doc,
    optionChoices,
    showPerformance: false,
    printWarningsToConsole: false,
    enabledFeatures: {
      horizontalAssemblies: false,
      roofFraming: false,
      modelComponents: false,
      wallPlanes: false,
      ...enabledFeatures,
    },
  });

  return await makeFaceDiffTestHashMaps(building, useExportPipelineGeometry, buildMaterialGeometry);
}

export const Route = createFileRoute("/debug/face-diff-viewer")({
  component: withAuthentication(FaceDiffViewer, { superAdmin: true }),
});
